import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import { ConfirmModal } from "@/components/ConfirmModal";
import {
  Plus, Pencil, Eye, Copy, Upload, Download, Archive, Trash2, Inbox,
  Loader2, X, FileText, Lock, ExternalLink, MoreHorizontal,
} from "lucide-react";

const STATUS_CLS = {
  published: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  unpublished: "bg-slate-600/20 text-slate-400 border-slate-600/30",
  archived: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function Forms() {
  const nav = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({ name_en: "", name_fr: "" });
  const [confirm, setConfirm] = useState({ isOpen: false });
  const [toast, setToast] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);

  const notify = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };

  const fetchForms = () => {
    setLoading(true); setError(false);
    axios.get(`${API}/admin/forms`, { withCredentials: true })
      .then((r) => setForms(r.data.forms || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchForms(); }, []);

  const doAction = async (fn, msg) => {
    setMenuOpen(null);
    try { await fn(); notify(msg); fetchForms(); }
    catch (e) { notify(e.response?.data?.detail || "Action failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/admin/forms`, newForm, { withCredentials: true });
      setShowCreate(false); setNewForm({ name_en: "", name_fr: "" });
      nav(`/admin/forms/${r.data.id}/edit`);
    } catch (e2) { notify(e2.response?.data?.detail || "Create failed"); }
  };

  return (
    <div data-testid="forms-page">
      {toast && <div className="fixed top-4 right-4 z-[200] bg-slate-800 border border-slate-700 text-white text-sm px-4 py-2.5 rounded-xl shadow-xl" data-testid="forms-toast">{toast}</div>}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <p className="text-sm text-slate-400 max-w-xl">Manage every website form — content, fields and submissions — without touching code. System forms cannot be deleted.</p>
        <button onClick={() => setShowCreate(true)} data-testid="create-form-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20 whitespace-nowrap">
          <Plus size={18} /> New Form
        </button>
      </div>

      <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-visible">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="forms-table">
            <thead>
              <tr className="bg-slate-950/50 border-b border-slate-800">
                {["Form", "Type", "Route", "Status", "Submissions", "Updated", "By", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500"><Loader2 className="animate-spin mx-auto" /></td></tr>
              ) : error ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center">
                  <p className="text-red-400 mb-3">Failed to load forms.</p>
                  <button onClick={fetchForms} className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm" data-testid="forms-retry">Retry</button>
                </td></tr>
              ) : forms.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center"><FileText className="mx-auto text-slate-700 mb-3" size={40} /><p className="text-slate-500">No forms yet.</p></td></tr>
              ) : forms.map((f) => (
                <tr key={f.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`form-row-${f.key}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white text-sm">{f.name_en}</span>
                      {f.is_protected_auth && <Lock size={13} className="text-amber-400" title="Protected auth form" />}
                      {f.managed_workflow === "submit_channel" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-400 border border-teal-500/20">managed</span>}
                    </div>
                    <div className="text-xs text-slate-500">{f.name_fr}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 capitalize">{f.type}</td>
                  <td className="px-4 py-3"><code className="text-xs text-slate-400">{f.public_route}</code></td>
                  <td className="px-4 py-3"><span className={cn("px-2 py-0.5 rounded border text-[11px] font-bold capitalize", STATUS_CLS[f.status])}>{f.status}</span></td>
                  <td className="px-4 py-3 text-sm text-slate-300">{f.submission_count}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(f.updated_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{f.updated_by === "system" ? "System" : (f.updated_by || "").slice(0, 8)}</td>
                  <td className="px-4 py-3 text-right relative">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/forms/${f.id}/edit`} data-testid={`form-edit-${f.key}`} className="p-2 text-slate-400 hover:text-blue-400" title="Edit"><Pencil size={16} /></Link>
                      <Link to={f.managed_workflow === "submit_channel" ? "/admin/channel-submissions" : `/admin/forms/${f.id}/submissions`}
                        data-testid={`form-submissions-${f.key}`} className="p-2 text-slate-400 hover:text-teal-400" title="Submissions"><Inbox size={16} /></Link>
                      <button onClick={() => setMenuOpen(menuOpen === f.id ? null : f.id)} data-testid={`form-menu-${f.key}`} className="p-2 text-slate-400 hover:text-white"><MoreHorizontal size={16} /></button>
                    </div>
                    {menuOpen === f.id && (
                      <div className="absolute right-4 top-12 z-20 w-44 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl py-1" data-testid={`form-menu-open-${f.key}`}>
                        {f.status !== "published" && <button onClick={() => doAction(() => axios.post(`${API}/admin/forms/${f.id}/publish`, {}, { withCredentials: true }), "Published")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-400 hover:bg-slate-700" data-testid={`form-publish-${f.key}`}><Upload size={14} /> Publish</button>}
                        {f.status === "published" && <button onClick={() => doAction(() => axios.post(`${API}/admin/forms/${f.id}/unpublish`, {}, { withCredentials: true }), "Unpublished")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700" data-testid={`form-unpublish-${f.key}`}><X size={14} /> Unpublish</button>}
                        <button onClick={() => doAction(() => axios.post(`${API}/admin/forms/${f.id}/duplicate`, {}, { withCredentials: true }), "Duplicated")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700" data-testid={`form-duplicate-${f.key}`}><Copy size={14} /> Duplicate</button>
                        {f.public_route && <a href={f.public_route} target="_blank" rel="noreferrer" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"><ExternalLink size={14} /> Preview</a>}
                        <button onClick={() => doAction(() => axios.post(`${API}/admin/forms/${f.id}/archive`, {}, { withCredentials: true }), "Archived")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"><Archive size={14} /> Archive</button>
                        {f.type !== "system" && (
                          <button onClick={() => setConfirm({ isOpen: true, title: "Delete Form", message: `Delete "${f.name_en}"? Submissions are preserved.`, onConfirm: () => doAction(() => axios.delete(`${API}/admin/forms/${f.id}`, { withCredentials: true }), "Deleted") })}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700" data-testid={`form-delete-${f.key}`}><Trash2 size={14} /> Delete</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="bg-slate-900 rounded-2xl w-full max-w-md border border-slate-800 overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-800 flex items-center justify-between">
              <h3 className="font-bold text-xl text-white">New Form</h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-500 hover:text-white"><X size={22} /></button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div>
                <label className="text-sm font-bold text-slate-400 block mb-1.5">Name (English)</label>
                <input required data-testid="new-form-name-en" className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20" value={newForm.name_en} onChange={(e) => setNewForm({ ...newForm, name_en: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-400 block mb-1.5">Name (French)</label>
                <input data-testid="new-form-name-fr" className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20" value={newForm.name_fr} onChange={(e) => setNewForm({ ...newForm, name_fr: e.target.value })} />
              </div>
              <button type="submit" data-testid="new-form-submit" className="w-full px-6 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-500 transition-all">Create & Edit</button>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={confirm.isOpen} title={confirm.title} message={confirm.message}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ isOpen: false }); }} onCancel={() => setConfirm({ isOpen: false })} />
    </div>
  );
}
